import type {
  AdminChatTraceResponse,
  TraceDiagnostic,
  TraceEvent,
  TraceRequest,
  TraceStatus,
} from '../types';
import { fallbackEventText, parseTracePayload } from './parse-payload';

export type TraceEventRole = 'primary' | 'auxiliary';

export interface TracePresentationEvent {
  event: TraceEvent;
  parsed: ReturnType<typeof parseTracePayload>;
  role: TraceEventRole;
  collapsed: boolean;
  duplicate: boolean;
  warning: boolean;
  text: string;
  navigationIndex: number;
}

export interface TraceRequestActivitySummary {
  primaryCount: number;
  auxiliaryCount: number;
  warningCount: number;
  lifecycleOnly: boolean;
}

export interface TraceTurnGroup {
  turnId: string;
  label: string;
  events: TracePresentationEvent[];
  collapsedEvents: TracePresentationEvent[];
  warningCount: number;
}

export interface TraceRequestGroup {
  requestId: string;
  label: string;
  request?: TraceRequest;
  legacy: boolean;
  status?: TraceStatus;
  turns: TraceTurnGroup[];
  warningCount: number;
}

export interface TracePresentation {
  requestGroups: TraceRequestGroup[];
  eventIndex: Map<string, TracePresentationEvent>;
  navigationOrder: TracePresentationEvent[];
  requestActivity: Map<string, TraceRequestActivitySummary>;
}

const LEGACY_REQUEST_ID = '__legacy__';
const UNASSIGNED_TURN_ID = '__unassigned_turn__';

export function buildTracePresentation(
  trace: AdminChatTraceResponse
): TracePresentation {
  const diagnostics = Array.isArray(trace.diagnostics) ? trace.diagnostics : [];
  const sortedRequests = sortTraceRequests(trace.requests);
  const requestOrder = new Map(
    sortedRequests.map((request, index) => [request.request_id, index])
  );
  const requestById = new Map(
    sortedRequests.map(request => [request.request_id, request])
  );
  const seenIdentity = new Set<string>();
  const navigationOrder: TracePresentationEvent[] = [];
  const eventIndex = new Map<string, TracePresentationEvent>();

  const events = [...trace.events].sort((left, right) => {
    const timeDiff = eventTime(left) - eventTime(right);
    if (timeDiff !== 0) return timeDiff;
    const requestDiff =
      (requestOrder.get(left.request_id || LEGACY_REQUEST_ID) ??
        Number.MAX_SAFE_INTEGER) -
      (requestOrder.get(right.request_id || LEGACY_REQUEST_ID) ??
        Number.MAX_SAFE_INTEGER);
    if (requestDiff !== 0) return requestDiff;
    const turnDiff = (left.turn_id || '').localeCompare(right.turn_id || '');
    if (turnDiff !== 0) return turnDiff;
    const rankDiff = eventSortRank(left) - eventSortRank(right);
    if (rankDiff !== 0) return rankDiff;
    return left.id.localeCompare(right.id);
  });

  const requestGroups = new Map<string, TraceRequestGroup>();

  for (const event of events) {
    const stableIdentity = eventIdentity(event);
    const duplicate = seenIdentity.has(stableIdentity);
    seenIdentity.add(stableIdentity);

    const parsed = parseTracePayload(event);
    const warning = hasWarning(event, diagnostics);
    const role = classifyEvent(event, parsed, warning, duplicate);
    const collapsed = role === 'auxiliary';
    const presentationEvent: TracePresentationEvent = {
      event,
      parsed,
      role,
      collapsed,
      duplicate,
      warning,
      text: eventText(event),
      navigationIndex: navigationOrder.length,
    };
    navigationOrder.push(presentationEvent);
    if (event.id) eventIndex.set(event.id, presentationEvent);

    const requestId = event.request_id || LEGACY_REQUEST_ID;
    let requestGroup = requestGroups.get(requestId);
    if (!requestGroup) {
      const request = requestById.get(requestId);
      requestGroup = {
        requestId,
        label:
          request?.label ||
          (requestId === LEGACY_REQUEST_ID ? 'Legacy / unassigned' : requestId),
        request,
        legacy: requestId === LEGACY_REQUEST_ID,
        status: request?.status,
        turns: [],
        warningCount: 0,
      };
      requestGroups.set(requestId, requestGroup);
    }
    if (warning) requestGroup.warningCount += 1;

    const turnId =
      event.turn_id || requestGroup.request?.turn_id || UNASSIGNED_TURN_ID;
    let turnGroup = requestGroup.turns.find(turn => turn.turnId === turnId);
    if (!turnGroup) {
      turnGroup = {
        turnId,
        label: turnId === UNASSIGNED_TURN_ID ? 'Unassigned turn' : turnId,
        events: [],
        collapsedEvents: [],
        warningCount: 0,
      };
      requestGroup.turns.push(turnGroup);
    }
    if (warning) turnGroup.warningCount += 1;
    if (collapsed) {
      turnGroup.collapsedEvents.push(presentationEvent);
    } else {
      turnGroup.events.push(presentationEvent);
    }
  }

  return {
    requestGroups: sortRequestGroups([...requestGroups.values()], requestOrder),
    eventIndex,
    navigationOrder,
    requestActivity: summarizeRequestActivity(requestGroups),
  };
}

export function sortTraceRequests(requests: TraceRequest[]): TraceRequest[] {
  return [...requests].sort((left, right) => {
    const startedDiff = requestStartedTime(left) - requestStartedTime(right);
    if (startedDiff !== 0) return startedDiff;
    const completedDiff =
      requestCompletedTime(left) - requestCompletedTime(right);
    if (completedDiff !== 0) return completedDiff;
    return left.request_id.localeCompare(right.request_id);
  });
}

function classifyEvent(
  event: TraceEvent,
  parsed: ReturnType<typeof parseTracePayload>,
  warning: boolean,
  duplicate: boolean
): TraceEventRole {
  if (
    event.kind === 'system_event' &&
    isLifecycleEvent(event) &&
    !event.warning
  )
    return 'auxiliary';
  if (warning || event.status !== 'success') return 'primary';
  if (duplicate) return 'auxiliary';
  if (
    parsed.kind === 'billing_router' ||
    parsed.kind === 'tool_result_message'
  ) {
    return 'auxiliary';
  }
  if (event.kind === 'persist_event') return 'auxiliary';
  if (
    event.kind === 'user_message' ||
    event.kind === 'assistant_message' ||
    event.kind === 'llm_call' ||
    event.kind === 'tool_call'
  ) {
    return 'primary';
  }
  if (event.kind === 'turn_marker') return warning ? 'primary' : 'auxiliary';
  return parsed.kind === 'generic_text' && !parsed.text
    ? 'auxiliary'
    : 'primary';
}

function hasWarning(event: TraceEvent, diagnostics: TraceDiagnostic[]) {
  if (event.warning) return true;
  if (event.status && event.status !== 'success') return true;
  if (event.quality && event.quality !== 'exact') return true;
  return diagnostics.some(diagnostic =>
    diagnostic.affected_event_ids?.includes(event.id)
  );
}

function isLifecycleEvent(event: TraceEvent) {
  const type = String(event.details?.event_type || event.title || '');
  return [
    'created',
    'queued',
    'status_changed',
    'session_run.claimed',
    'session_run.completed',
    'activation_round_completed',
  ].includes(type);
}

function eventSortRank(event: TraceEvent) {
  if (event.kind === 'assistant_message') {
    const parsed = parseTracePayload(event);
    if (parsed.kind === 'assistant_tool_calls') return 20;
    if (parsed.kind === 'tool_result_message') return 40;
    return 50;
  }
  if (event.kind === 'tool_call') return 30;
  if (event.kind === 'llm_call') return 10;
  if (event.kind === 'user_message') return 0;
  return 60;
}

function sortRequestGroups(
  groups: TraceRequestGroup[],
  requestOrder: Map<string, number>
) {
  return groups.sort((left, right) => {
    const orderDiff =
      (requestOrder.get(left.requestId) ?? Number.MAX_SAFE_INTEGER) -
      (requestOrder.get(right.requestId) ?? Number.MAX_SAFE_INTEGER);
    if (orderDiff !== 0) return orderDiff;
    return left.requestId.localeCompare(right.requestId);
  });
}

function summarizeRequestActivity(
  groups: Map<string, TraceRequestGroup>
): Map<string, TraceRequestActivitySummary> {
  const summary = new Map<string, TraceRequestActivitySummary>();
  for (const group of groups.values()) {
    const events = group.turns.flatMap(turn => [
      ...turn.events,
      ...turn.collapsedEvents,
    ]);
    const primaryCount = events.filter(item => item.role === 'primary').length;
    const auxiliaryCount = events.filter(
      item => item.role === 'auxiliary'
    ).length;
    summary.set(group.requestId, {
      primaryCount,
      auxiliaryCount,
      warningCount: group.warningCount,
      lifecycleOnly:
        primaryCount === 0 &&
        auxiliaryCount > 0 &&
        events.every(item => isLifecycleEvent(item.event)),
    });
  }
  return summary;
}

function requestStartedTime(request: TraceRequest) {
  const timestamp = new Date(request.started_at || '').getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
}

function requestCompletedTime(request: TraceRequest) {
  const timestamp = new Date(request.completed_at || '').getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
}

function eventIdentity(event: TraceEvent) {
  return [
    event.kind,
    event.request_id || '',
    event.turn_id || '',
    event.title || '',
    event.at || '',
    event.summary || '',
  ].join('|');
}

function eventText(event: TraceEvent) {
  return [
    event.id,
    event.kind,
    event.title,
    event.summary,
    event.warning,
    event.request_id,
    event.turn_id,
    fallbackEventText(event),
    JSON.stringify(event.details || {}),
  ]
    .filter(Boolean)
    .join(' ');
}

function eventTime(event: TraceEvent) {
  const timestamp = new Date(event.at).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}
